import React from 'react';
import {
  TRACK_COLOR,
  defaultGaugeSize,
  gaugeGeometry,
  technicianScoreView,
} from './technicianScore';

/**
 * Circular technician score gauge.
 *
 * Compact (table): numeric score only, 44px default.
 * Expanded: score, /100, and BAD/OK/GOOD.
 */
const TechnicianScoreGauge = ({
  score,
  size,
  variant = 'compact',
  className = '',
  labelPrefix = 'Technician score',
}) => {
  const resolvedSize = size ?? defaultGaugeSize(variant);
  const expanded = variant === 'expanded';
  const view = technicianScoreView(score, { labelPrefix });
  const { cx, cy, r, strokeWidth, circumference } = gaugeGeometry(resolvedSize);
  const showArc = view.available && view.value > 0;
  const complete = view.available && view.value === 100;
  const dashOffset = circumference * (1 - view.fraction);
  const scoreFont = expanded
    ? Math.round(resolvedSize * 0.28)
    : Math.round(resolvedSize * (15 / 44));
  const inset = strokeWidth + (resolvedSize * (2 / 44));

  return (
    <div
      className={`relative inline-flex shrink-0 ${className}`.trim()}
      style={{ width: resolvedSize, height: resolvedSize }}
      role="img"
      aria-label={view.ariaLabel}
      title={view.tooltip}
    >
      <svg
        width={resolvedSize}
        height={resolvedSize}
        viewBox={`0 0 ${resolvedSize} ${resolvedSize}`}
        aria-hidden="true"
        focusable="false"
        className="absolute inset-0"
        overflow="visible"
      >
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke={TRACK_COLOR}
          strokeWidth={strokeWidth}
        />
        {showArc ? (
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={view.colors.arc}
            strokeWidth={strokeWidth}
            strokeLinecap={complete ? 'butt' : 'round'}
            transform={`rotate(-90 ${cx} ${cy})`}
            {...(complete
              ? {}
              : {
                  strokeDasharray: `${circumference} ${circumference}`,
                  strokeDashoffset: dashOffset,
                })}
          />
        ) : null}
      </svg>
      <div
        className="absolute flex flex-col items-center justify-center rounded-full"
        style={{
          top: inset,
          right: inset,
          bottom: inset,
          left: inset,
          backgroundColor: view.colors.tint,
        }}
      >
        <span
          className="select-text font-bold leading-none text-gray-900"
          style={{
            fontSize: scoreFont,
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: view.display === '100' || view.display === '--' ? '-0.04em' : '-0.02em',
          }}
        >
          {view.display}
        </span>
        {expanded && view.available ? (
          <span className="mt-0.5 text-[11px] leading-none text-gray-500">/100</span>
        ) : null}
        {expanded ? (
          <span
            className="mt-1 text-[11px] font-semibold uppercase leading-none"
            style={{ color: view.colors.statusText }}
          >
            {view.statusLabel}
          </span>
        ) : null}
      </div>
    </div>
  );
};

export default TechnicianScoreGauge;
